import type { Observable } from "@legendapp/state";
import { useValue } from "@legendapp/state/react";
import type { LanesColumn } from "@repo/lanes/types";
import { format } from "@repo/shared/helpers/format";
import type { ColumnsConfig } from "@repo/shared/rift/types";
import type {
  ColumnFormat,
  DateFormat,
} from "@repo/shared/types/column-format";
import {
  type GraphConfig,
  isCartesianChart,
  isPolarChart,
  type ShowValuesMode,
} from "@repo/shared/types/graph-config";
import {
  baseColumnAccessor,
  resolveColumnFormat as resolveConfigColumnFormat,
} from "@repo/shared/utils/merge-columns-configs";
import AGChart from "@repo/ui/components/chart";
import { Icon } from "@repo/ui/components/icon";
import type { AgChartOptions, FormatterParams } from "ag-charts-types";
import { useMemo } from "react";

import { buildArcValueLabelFormatter } from "~/components/rift-chart-pie-label";
import { getRiftDescriptor, useRiftQuery } from "~/stores/dataset-rifts";
import { datasets$ } from "~/stores/datasets";
import { theme$ } from "~/stores/theme.store";

/**
 * Resolve the FormatType for a formatter callback. For series-level sources (tooltips,
 * series labels) `params.key` identifies the column directly. For chart-level sources
 * (axis labels, crosshairs, legends) `key` is undefined — we fall back to the static
 * `keyMap`, then to `boundSeries` (picking a format only when every bound series shares
 * the same one, so the axis isn't labelled ambiguously).
 */
const resolveColumnFormat = (
  params: FormatterParams,
  columnFormats: Record<string, ColumnFormat>,
  keyMap: Record<string, string | undefined>,
): ColumnFormat | undefined => {
  const fromKey = (key: string): ColumnFormat | undefined => columnFormats[key];

  if (params.key != null) {
    const direct = fromKey(String(params.key));
    if (direct) return direct;
    // Fall through to keyMap for any property→result-key indirection.
    const remapped = keyMap[String(params.key)];
    if (remapped) {
      const resolved = fromKey(remapped);
      if (resolved) return resolved;
    }
  }

  const staticKey = keyMap[params.property];
  if (staticKey) {
    const resolved = fromKey(staticKey);
    if (resolved) return resolved;
  }

  if ("boundSeries" in params && params.boundSeries?.length) {
    const keys = params.boundSeries
      .map((s) => s.key)
      .filter((key) => Boolean(columnFormats[key]));
    const formats = new Set(
      keys.map((key) => JSON.stringify(columnFormats[key])),
    );
    // Only label the shared axis/legend when every bound series agrees on the
    // format, so it isn't rendered ambiguously.
    if (formats.size === 1 && keys[0]) return fromKey(keys[0]);
  }

  return undefined;
};

/**
 * Build a chart-level AG Charts formatter that delegates to the shared `format()` helper.
 * Uses "short" style for compact elements (axis labels, legends) and "long" for tooltips.
 */
const buildFormatter = (
  columnFormats: Record<string, ColumnFormat>,
  keyMap: Record<string, string | undefined>,
) => {
  return (params: FormatterParams): string | undefined => {
    const resolved = resolveColumnFormat(params, columnFormats, keyMap);
    if (!resolved) return undefined;

    const style: "long" | "short" =
      params.source === "tooltip" ? "long" : "short";

    return format({
      value: params.value,
      format: resolved,
      style,
    });
  };
};

/** Build AG Charts label config from the show values mode. */
const buildLabelConfig = (mode: ShowValuesMode) => {
  switch (mode) {
    case "ALWAYS":
      return { enabled: true };
    case "HOVER_SERIES":
      return {
        enabled: true,
        itemStyler: (params: any) => {
          switch (params.highlightState) {
            case "highlighted-series":
            case "unhighlighted-item":
              return {};
            case "highlighted-item":
              return { fontWeight: "bold" };
            default:
              return { color: "transparent" };
          }
        },
      };
    case "HOVER_ITEM":
      return {
        enabled: true,
        itemStyler: (params: any) => {
          if (params.highlightState === "highlighted-item") return {};
          return { color: "transparent" };
        },
      };
    default:
      return { enabled: false };
  }
};

/**
 * Gates a pie's callout lines in step with its callout labels.
 *
 * `updateCalloutLineNodes` keys the connector off the label's `hidden` flag,
 * not its color, and `buildLabelConfig`'s hover modes hide text by turning it
 * transparent. Without a matching styler the chart would draw a starburst of
 * lines pointing at invisible text until something is hovered.
 *
 * Returns `undefined` for the non-hover modes, which keeps the theme defaults.
 */
const buildCalloutLineConfig = (mode: ShowValuesMode) => {
  if (mode !== "HOVER_SERIES" && mode !== "HOVER_ITEM") return undefined;
  return {
    itemStyler: (params: any) => {
      const state = params.highlightState;
      if (state === "highlighted-item") return {};
      if (
        mode === "HOVER_SERIES" &&
        (state === "highlighted-series" || state === "unhighlighted-item")
      ) {
        return {};
      }
      return { color: "transparent" };
    },
  };
};

/** AG Charts time interval (object notation), used as the `unit-time` axis band size. */
type AgTimeInterval = {
  unit: "day" | "month" | "year";
  step?: number;
  /** Anchor date for stepped intervals (e.g. a Monday for week-aligned bands). */
  epoch?: Date;
  utc: boolean;
};

/**
 * A known Monday in UTC. DuckDB `DATE_TRUNC('week', ...)` starts weeks on
 * Monday, so weekly bands must be anchored to a Monday for data points to land
 * on the correct band (AG's default day-step epoch is a Thursday).
 */
const MONDAY_EPOCH = new Date(Date.UTC(2024, 0, 1));

/**
 * Map a date {@link FormatType} to the `unit-time` axis band size so the axis
 * produces exactly one evenly-spaced band per period (matching the DuckDB
 * DATE_TRUNC the chart rift applies). The `unit-time` scale floors each datum
 * to this unit, so even sub-period timestamps collapse onto a single band
 * instead of rendering as duplicate day-precision labels.
 *
 * `AgTimeIntervalUnit` only supports day/month/year, so week and quarter are
 * expressed as stepped day/month intervals. Display-only formats (DATETIME /
 * TIME / RELATIVE) return `undefined`, which falls the axis back to category.
 */
const dateFormatToTimeInterval = (
  dateFormat: DateFormat | undefined,
): AgTimeInterval | undefined => {
  switch (dateFormat) {
    case "DATE":
      return { unit: "day", utc: true };
    case "WEEK":
      return { unit: "day", step: 7, epoch: MONDAY_EPOCH, utc: true };
    case "MONTH":
      return { unit: "month", utc: true };
    case "QUARTER":
      return { unit: "month", step: 3, utc: true };
    case "YEAR":
      return { unit: "year", utc: true };
    default:
      return undefined;
  }
};

/**
 * Resolve an axis title from explicit config vs column-name default.
 * - `""` (explicit empty) → user cleared the label → no title
 * - non-empty string → user override
 * - `undefined` → use the fallback default derived from column names
 */
const resolveAxisTitle = (
  explicit: string | undefined,
  fallback: string | undefined,
): string | undefined => {
  if (explicit === "") return undefined;
  if (explicit != null) return explicit;
  return fallback;
};

/**
 * Builds AG Charts options from chart-rift data. The data is already
 * pre-processed by DuckDB (sorted, grouped, aggregated, null-filtered)
 * so this function focuses on mapping columns to AG Charts config.
 *
 * For pie charts the chart rift returns `sectorLabel` and `angle` columns;
 * for cartesian charts it returns the original column names with data
 * already grouped/sorted as needed.
 */
function buildChartOptions(
  data: Record<string, unknown>[],
  columns: string[],
  columnMeta: Record<string, LanesColumn> | null,
  graphConfig?: GraphConfig,
  columnNames?: Record<string, string>,
  columnFormats?: Record<string, ColumnFormat>,
  runColumns?: Record<string, LanesColumn> | null,
): AgChartOptions | null {
  if (!data?.length || !columns.length) return null;

  const chartType = graphConfig?.type ?? "line";

  // Infer numeric columns from DuckDB column metadata when available,
  // falling back to first-row sampling for backwards compatibility.
  const numericCols = new Set<string>();
  if (columnMeta) {
    for (const [name, col] of Object.entries(columnMeta)) {
      if (col.type === "NUMBER") numericCols.add(name);
    }
  } else if (data[0]) {
    for (const name of columns) {
      if (typeof data[0][name] === "number") numericCols.add(name);
    }
  }

  if (isCartesianChart(chartType)) {
    const xKey = graphConfig?.xKey;
    const yKeys = graphConfig?.yKeys?.length ? graphConfig.yKeys : [];
    const yColors = graphConfig?.yColors ?? {};
    const direction = graphConfig?.direction ?? "vertical";
    const isHorizontal = direction === "horizontal";

    if (!xKey || yKeys.length === 0) return null;

    const yTypes = graphConfig?.yTypes ?? {};
    const stacked = graphConfig?.stacked ?? false;
    const showValues = graphConfig?.showValues ?? {};

    const series = yKeys.map((yKey) => {
      const seriesType = yTypes[yKey] ?? chartType;
      const color = yColors[yKey];
      const seriesShowValuesMode: ShowValuesMode = showValues[yKey] ?? "NONE";
      return {
        type: seriesType,
        xKey,
        yKey,
        yName: columnNames?.[yKey] ?? yKey,
        ...(stacked && { stacked: true }),
        ...(color && { fill: color, stroke: color }),
        ...(seriesType === "bar" &&
          isHorizontal && { direction: "horizontal" as const }),
        ...((seriesType === "bar" ||
          seriesType === "line" ||
          seriesType === "area") && {
          label: buildLabelConfig(seriesShowValuesMode),
        }),
      };
    });

    // Prefer the original column type from the dataset run, then DuckDB's
    // reported type. Auto-aggregate fallbacks and user-authored CASTs can mask
    // a temporal column as STRING; the run columns remain the source of truth.
    // A user-set DATE format type also forces a time-based (unit-time) axis.
    const xColType = runColumns?.[xKey]?.type ?? columnMeta?.[xKey]?.type;
    const xFormat = columnFormats?.[xKey];
    const isTimeX =
      xColType === "DATE" ||
      xFormat?.kind === "DATE" ||
      (!xColType &&
        (() => {
          const xSample = data[0]?.[xKey];
          return (
            xSample instanceof Date ||
            (typeof xSample === "string" && !Number.isNaN(Date.parse(xSample)))
          );
        })());

    // Date line/area/scatter charts use a `unit-time` axis: discrete,
    // evenly-spaced, time-aware bands (the community-bundle equivalent of the
    // enterprise-only `ordinal-time` axis — using `ordinal-time` here silently
    // falls back to a continuous `time` axis, which produces duplicate labels
    // and edge-anchored points). Bars keep their forced category axis.
    //
    // The `unit-time` band size is driven by the top-level `unit`, which the
    // axis floors each datum to — so one band per period, and sub-period
    // timestamps collapse onto their band instead of duplicating labels. We
    // only enable it for date formats that map to a concrete unit; otherwise
    // (DATETIME / TIME / display-only) we fall back to a plain category axis.
    const hasBarSeries = series.some((s) => s.type === "bar");
    const xTimeUnit =
      isTimeX && !hasBarSeries
        ? dateFormatToTimeInterval(
            xFormat?.kind === "DATE" ? xFormat.dateFormat : undefined,
          )
        : undefined;
    const useUnitTime = xTimeUnit != null;

    const categoryPosition = isHorizontal ? "left" : "bottom";
    const numberPosition = isHorizontal ? "bottom" : "left";

    const xTitle =
      (graphConfig?.xAxisLabel?.enabled ?? true)
        ? resolveAxisTitle(
            graphConfig?.xAxisLabel?.title,
            columnNames?.[xKey] ?? xKey,
          )
        : undefined;
    const yTitle =
      (graphConfig?.yAxisLabel?.enabled ?? true)
        ? resolveAxisTitle(
            graphConfig?.yAxisLabel?.title,
            yKeys.map((k) => columnNames?.[k] ?? k).join(" / "),
          )
        : undefined;

    const showXGridLines = graphConfig?.xGridLines ?? false;
    const showYGridLines = graphConfig?.yGridLines ?? false;
    const showAxisTicks = graphConfig?.axisTicks ?? false;
    const showAxisLines = graphConfig?.axisLines ?? false;

    const axes = {
      x: {
        type: useUnitTime ? "unit-time" : "category",
        position: categoryPosition,
        // The `unit-time` band size. AGChart's wrapper adds `utc: true`.
        ...(xTimeUnit && { unit: xTimeUnit }),
        // Horizontal bars put the category axis on the left, where long text
        // labels would otherwise overflow and get clipped by the container.
        // Truncate them with an ellipsis and let the axis reserve up to half the
        // chart width (vs. the 0.3 default). AG Charts does not subtract the axis
        // title's width from the label-truncation limit, so a title on this axis
        // always overflows once labels are long — the title is dropped below for
        // horizontal charts (the category labels make it redundant anyway), which
        // lets AG size the axis to the labels and squish the plot area to fit.
        ...(isHorizontal && {
          label: { wrapping: "never" as const, truncate: true },
        }),
        ...(xTitle && { title: { text: xTitle, enabled: true } }),
        ...(showXGridLines && { gridLine: { enabled: true } }),
        ...(showAxisTicks && { tick: { enabled: true } }),
        ...(showAxisLines && { line: { enabled: true } }),
      },
      y: {
        type: "number",
        position: numberPosition,
        ...(yTitle && { title: { text: yTitle, enabled: true } }),
        ...(showYGridLines && { gridLine: { enabled: true } }),
        ...(showAxisTicks && { tick: { enabled: true } }),
        ...(showAxisLines && { line: { enabled: true } }),
      },
    };

    const keyMap: Record<string, string | undefined> = { x: xKey };

    const tooltipEnabled = graphConfig?.tooltip?.enabled ?? true;

    return {
      data,
      series,
      axes,
      // The shared theme adds `seriesArea.padding.left: 24`. AG Charts folds
      // that into the left axis's label offset but not its (capped) thickness
      // budget, so horizontal category labels overflow ~24px and get clipped.
      // Drop it for horizontal charts — `label.spacing` still keeps a gap — so
      // labels fit within the reserved width and the plot area squishes.
      ...(isHorizontal && { seriesArea: { padding: { left: 0 } } }),
      ...(columnFormats &&
        Object.keys(columnFormats).length > 0 && {
          formatter: buildFormatter(columnFormats, keyMap),
        }),
      ...(graphConfig?.legend && {
        legend: {
          enabled: graphConfig.legend.enabled ?? true,
          position: graphConfig.legend.position ?? "top",
        },
      }),
      ...(!tooltipEnabled && { tooltip: { enabled: false } }),
    } as unknown as AgChartOptions;
  }

  // Polar charts (pie) — the chart rift returns pre-aggregated data with
  // "sectorLabel" and "angle" columns. When those exist in the result we
  // use them directly; otherwise fall back to the user's graphConfig keys.
  if (isPolarChart(chartType)) {
    const hasPieColumns =
      columns.includes("sectorLabel") && columns.includes("angle");
    const angleKey = hasPieColumns ? "angle" : graphConfig?.angleKey;
    const sectorLabelKey = hasPieColumns
      ? "sectorLabel"
      : graphConfig?.sectorLabelKey;
    // `showValues` is keyed by the user's column (`graphConfig.angleKey`), not
    // by the `"angle"` alias the rift emits, so the mode is read off the config
    // key. The sidepane and `sanitize-agent-block` both write it that way.
    const showValuesMap = graphConfig?.showValues ?? {};
    const showValuesKey = graphConfig?.angleKey ?? angleKey;
    const polarShowValuesMode: ShowValuesMode = showValuesKey
      ? (showValuesMap[showValuesKey] ?? "NONE")
      : "NONE";

    if (!angleKey) return null;

    // `columnFormats` is keyed by the rift result column names (the aliased
    // "angle"/"sectorLabel" when hasPieColumns, otherwise the graphConfig
    // keys). Map AG Charts property names → those result keys so chart-level
    // sources (legend, labels) without a `params.key` resolve the format.
    const keyMap: Record<string, string | undefined> = {
      angle: angleKey,
      sectorLabel: sectorLabelKey,
      label: sectorLabelKey,
    };

    // Value labels ride the CALLOUT label, not the sector label. AG only makes
    // a sector label visible when all four corners of its text box fall inside
    // the wedge, which a "value (share%)" string clears on almost no slice of a
    // 0.7-inner-radius donut, so sector labels rendered nothing at all.
    //
    // Leave `avoidCollisions` at its default of true. It reads like a style
    // toggle but it is the layout switch: `computeLabelsBBox` returns null when
    // it is off, and the polar layout reads that as "no labels" and keeps the
    // pie at full radius — which would put every callout outside the series box.
    // On, the layout shrinks the pie to make room and offsets overlapping
    // labels, at the cost of hiding ones it still cannot place.
    //
    // Taking over the callout would otherwise hijack two other surfaces that
    // fall back to it: the legend (`getLegendData`) and the tooltip's label
    // (`labelValues.legendItem ?? labelValues.callout ?? labelValues.sector`).
    // Pointing `legendItemKey` at the category column outranks the callout in
    // both, so they keep reading exactly as they do today.
    const arcValueLabel =
      polarShowValuesMode === "NONE"
        ? null
        : {
            ...buildLabelConfig(polarShowValuesMode),
            minAngle: 0,
            formatter: buildArcValueLabelFormatter(
              data,
              angleKey,
              columnFormats?.[angleKey],
            ),
          };
    const arcValueLine = buildCalloutLineConfig(polarShowValuesMode);

    const tooltipEnabled = graphConfig?.tooltip?.enabled ?? true;

    return {
      data,
      series: [
        {
          type: "donut",
          angleKey,
          sectorLabelKey,
          sectorLabel: { enabled: false },
          ...(arcValueLabel && {
            calloutLabelKey: angleKey,
            calloutLabel: arcValueLabel,
            ...(arcValueLine && { calloutLine: arcValueLine }),
            ...(sectorLabelKey && { legendItemKey: sectorLabelKey }),
          }),
        },
      ],
      ...(columnFormats &&
        Object.keys(columnFormats).length > 0 && {
          formatter: buildFormatter(columnFormats, keyMap),
        }),
      ...(graphConfig?.legend && {
        legend: {
          enabled: graphConfig.legend.enabled ?? true,
          position: graphConfig.legend.position ?? "top",
        },
      }),
      ...(!tooltipEnabled && { tooltip: { enabled: false } }),
    } as unknown as AgChartOptions;
  }

  return null;
}

type RiftChartProps = {
  riftId: string;
  columnsConfig$: Observable<ColumnsConfig>;
  graphConfig$: Observable<GraphConfig>;
};

export const RiftChart = ({
  riftId,
  columnsConfig$,
  graphConfig$,
}: RiftChartProps) => {
  const query = useRiftQuery(riftId);
  const theme = useValue(theme$.theme);
  const datasetId = getRiftDescriptor(riftId)?.datasetId ?? null;
  const numRows = query.data?.data?.numRows ?? 0;

  // Materialise rows (and coerce chart-hostile bigints to numbers) once per
  // result load. `query.data` is TanStack Query state — not an observable — so
  // this belongs in `useMemo`, keeping the full-table `toRows()` off the
  // per-config-change render path that the `useValue` below runs on.
  const chartData = useMemo<Record<string, unknown>[] | null>(() => {
    const currentData = query.data?.data ?? null;
    const currentColumnMeta = query.data?.columns ?? null;
    if (!currentData) return null;

    const currentRows = currentData.toRows();

    // DuckDB integer aggregates (COUNT -> BIGINT, SUM(int) -> HUGEINT) surface
    // as JS `bigint`, which AG Charts' numeric axis cannot plot (it renders
    // "No data available"). Coerce numeric columns' bigint values to `number`
    // for the chart only; rows without bigint values are passed through as-is
    // to avoid needless allocations.
    const numericKeys = new Set<string>();
    if (currentColumnMeta) {
      for (const [key, col] of Object.entries(currentColumnMeta)) {
        if (col.type === "NUMBER") numericKeys.add(key);
      }
    }
    if (numericKeys.size === 0) return currentRows;
    return currentRows.map((row) => {
      let next: Record<string, unknown> | null = null;
      for (const key of numericKeys) {
        if (typeof row[key] === "bigint") {
          if (!next) next = { ...row };
          next[key] = Number(row[key] as bigint);
        }
      }
      return next ?? row;
    });
  }, [query.data]);

  const chartView = useValue(() => {
    const currentColumnMeta = query.data?.columns ?? null;
    const graphConfig = graphConfig$.get();
    const columnsConfig = columnsConfig$.get();

    if (!chartData) return null;

    // Prefer original column types from the dataset run for format inference.
    // Aggregations that emit text (CONCAT) and user-authored CASTs can still
    // degrade type fidelity at the rift edge; the run columns remain the
    // source of truth.
    const runColumns = datasetId ? datasets$[datasetId].columns.get() : null;

    const columnDisplayNames: Record<string, string> = {};
    if (columnsConfig) {
      for (const [key, cfg] of Object.entries(columnsConfig)) {
        if (cfg?.displayName) columnDisplayNames[key] = cfg.displayName;
      }
    }

    // Pie charts alias their result columns to `angle`/`sectorLabel`, but
    // `columnsConfig` and `runColumns` are keyed by the original column names.
    // Map the aliased result keys back to their source columns so the format
    // resolution below inherits the user's configured format/type.
    const sourceKeyFor = (key: string): string => {
      if (graphConfig && isPolarChart(graphConfig.type)) {
        if (key === "angle") return graphConfig.angleKey ?? key;
        if (key === "sectorLabel") return graphConfig.sectorLabelKey ?? key;
      }
      return key;
    };

    // Resolve a format for every result column, inheriting from the base
    // column for aggregate aliases (`revenue::SUM` → `revenue`) and coercing
    // any legacy persisted shapes. Falls back to the primitive default.
    const columnFormats: Record<string, ColumnFormat> = {};
    if (currentColumnMeta) {
      for (const [key, col] of Object.entries(currentColumnMeta)) {
        const sourceKey = sourceKeyFor(key);
        const baseKey = baseColumnAccessor(sourceKey);
        const colType =
          runColumns?.[sourceKey]?.type ??
          runColumns?.[baseKey]?.type ??
          col.type;
        columnFormats[key] = resolveConfigColumnFormat(
          columnsConfig,
          sourceKey,
          colType,
        );
      }
    }

    const cols = Object.keys(currentColumnMeta ?? {});

    // Resilience guard: a y-axis column is only plottable when its REALIZED
    // post-rift output type is numeric. A column made numeric via COUNT but
    // later switched back to CONCAT surfaces here as non-numeric — we drop it
    // from the plotted series and surface a warning, leaving graphConfig.yKeys
    // intact so flipping the op back to a count/sum restores the series.
    const isNumericColumn = (key: string) =>
      currentColumnMeta?.[key]?.type === "NUMBER";

    let effectiveGraphConfig = graphConfig;
    let invalidYKeys: string[] = [];
    if (
      graphConfig &&
      isCartesianChart(graphConfig.type) &&
      graphConfig.yKeys?.length
    ) {
      invalidYKeys = graphConfig.yKeys.filter((k) => !isNumericColumn(k));
      if (invalidYKeys.length > 0) {
        effectiveGraphConfig = {
          ...graphConfig,
          yKeys: graphConfig.yKeys.filter(isNumericColumn),
        };
      }
    }

    const options = buildChartOptions(
      chartData,
      cols,
      currentColumnMeta,
      effectiveGraphConfig,
      columnDisplayNames,
      columnFormats,
      runColumns,
    );

    return { options, invalidYKeys };
  });

  const chartOptions = chartView?.options ?? null;
  const invalidYKeys = chartView?.invalidYKeys ?? [];

  if (
    !riftId ||
    (query.fetchStatus === "idle" && !query.data && !query.isError)
  ) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center p-6 text-center">
        <Icon
          name="data-table"
          className="mb-4 size-12 text-muted-foreground"
        />
        <p className="font-medium text-muted-foreground text-sm">
          Run a query to show the chart
        </p>
      </div>
    );
  }
  if (!chartOptions) {
    // All configured y-axis columns lost their numeric type — show a specific,
    // actionable message instead of the generic "select columns" prompt.
    if (query.data && invalidYKeys.length > 0) {
      return (
        <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center gap-2 p-6 text-center text-muted-foreground text-sm">
          <Icon name="warning" className="size-5 text-destructive" />
          {buildNonNumericWarning(invalidYKeys)}
        </div>
      );
    }
    let message = "Loading\u2026";
    if (query.data) {
      message = numRows
        ? "Select columns in the sidebar to build your chart."
        : "No data to chart. Try adjusting your filters.";
    } else if (query.isError && !query.isPlaceholderData) {
      message = "Chart query failed.";
    }
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col items-center justify-center p-6 text-center text-muted-foreground text-sm">
        {message}
      </div>
    );
  }
  // Some series still plot, but at least one y-key is no longer numeric: render
  // the chart with a warning banner so the user understands the missing series.
  if (invalidYKeys.length > 0) {
    return (
      <div className="relative flex h-full min-h-0 flex-1 flex-col">
        <div className="flex items-center gap-2 px-3 py-1.5 text-muted-foreground text-xs">
          <Icon name="warning" className="size-3.5 shrink-0 text-destructive" />
          <span className="truncate">
            {buildNonNumericWarning(invalidYKeys)}
          </span>
        </div>
        <div className="relative min-h-0 flex-1">
          <AGChart options={chartOptions} theme={theme} />
        </div>
      </div>
    );
  }
  return <AGChart options={chartOptions} theme={theme} />;
};

/** Phrases the warning shown when a y-axis column is no longer numeric. */
const buildNonNumericWarning = (keys: string[]): string => {
  const list = keys.map((k) => `"${k}"`).join(", ");
  const plural = keys.length > 1;
  return `${list} ${plural ? "are" : "is"} no longer numeric. Set a count or sum aggregation to plot ${plural ? "them" : "it"} on the y-axis.`;
};
